import { supabase } from "./supabase";
import { webhookLimiter, formatRetryTime } from "@/utils/rateLimit";
import { logAudit } from "./auditLog";

// =============================================================================
// n8n
// =============================================================================
// PURPOSE: Authenticated and public N8N webhook trigger functions.
//
// ARCHITECTURE:
// - securelyTriggerN8n: Includes JWT from Supabase session (Authorization: Bearer)
// - triggerPublicWebhook: No auth headers (for public forms like job applications)
// - Rate limiting: 50 calls per minute (webhookLimiter)
// - Audit logging: All authenticated webhook calls logged to audit_logs
//
// SECURITY:
// - JWT verification happens in N8N workflows (extracts user_id from token)
// - CSRF protection: X-Requested-With header (blocks simple CSRF attacks)
// - All webhook URLs centralized in constants.ts (no hardcoded URLs)
//
// KEY DECISIONS:
// - Returns parsed JSON response from N8N (fallback to {success: true} if no JSON)
// - Rate limit checked BEFORE fetching session (fails fast)
// - Public webhooks fail silently (don't break user experience if N8N is down)
// =============================================================================

/**
 * N8N webhook payload interface - flexible structure for various webhook actions
 */
interface N8NWebhookPayload {
  action?: string;
  type?: string;
  resource_type?: string;
  id?: string;
  resource_id?: string;
  [key: string]: unknown; // Allow additional fields for different webhook types
}

/**
 * securelyTriggerN8n
 *
 * Sends a POST request to an n8n webhook with the current user's Auth Token.
 * This allows n8n to verify the identity of the requester before taking action.
 *
 * @param webhookUrl - The full URL of the n8n webhook (from constants.ts)
 * @param payload - JSON object of data to send (e.g., { id: '123' })
 */
export const securelyTriggerN8n = async (webhookUrl: string, payload: N8NWebhookPayload) => {
  // 1. Check rate limit first
  const rateLimit = webhookLimiter();
  if (!rateLimit.allowed) {
    const retryTime = formatRetryTime(rateLimit.retryAfter ?? 0);
    throw new Error(`Too many requests. Please try again in ${retryTime}.`);
  }

  // 2. Get the current session to extract the JWT (Access Token)
  const {
    data: { session },
  } = (await supabase?.auth.getSession()) || { data: { session: null } };

  if (!session?.access_token) {
    throw new Error("You must be logged in to perform this action.");
  }

  // 3. Check if URL is configured
  if (webhookUrl.includes("replace-with")) {
    throw new Error("System Configuration Error: Webhook URL not set.");
  }

  // 4. Log audit event for webhook action
  const action = payload?.action || "webhook_call";
  const resourceType = payload?.resource_type;
  const resourceId = payload?.id || payload?.resource_id;

  logAudit(action, resourceType, resourceId, {
    webhook_url: webhookUrl.split("/webhook/")[1], // Just the endpoint name
    payload_keys: Object.keys(payload),
  });

  try {
    // 5. Send Request to n8n
    const response = await fetch(webhookUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
        "X-Requested-With": "XMLHttpRequest",
        "X-Client-Version": "1.0.0",
      },
      body: JSON.stringify({
        ...payload,
        // We also send user_id in body for easier access in n8n nodes
        triggered_by_user: session.user.id,
        triggered_by_email: session.user.email,
      }),
    });

    if (!response.ok) {
      throw new Error("Request failed. Please try again.");
    }

    // 6. Return response (n8n must end with "Respond to Webhook" node)
    // We try/catch the json parsing just in case n8n returns plain text or nothing
    try {
      const data = await response.json();
      return data;
    } catch {
      return { success: true };
    }
  } catch {
    throw new Error("Operation failed. Please try again.");
  }
};

/**
 * triggerPublicWebhook
 *
 * Sends a POST request to an n8n webhook WITHOUT authentication headers.
 * Used for public forms like Job Applications or Client Inquiries.
 */
export const triggerPublicWebhook = async (
  webhookUrl: string,
  payload: Record<string, unknown>
) => {
  if (webhookUrl.includes("replace-with")) {
    return;
  }

  try {
    const response = await fetch(webhookUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "X-Client-Version": "1.0.0",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      return;
    }
  } catch {
    // Silent fail for public webhooks
  }
};
